import { formatCnpj } from "utils/federal-tax-id";
import { Avatar, Chip, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, Table, TableCell, TableContainer, TableRow } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import api from "services/api";
import { Page } from "v2/components";
import SearchIcon from '@mui/icons-material/Search';
import { useHistory } from "react-router-dom";
import { ChevronRight } from "@mui/icons-material";
import { StyledTextField, Subtitle, Title } from "./styled";
import { useAuth } from "contexts/auth";

interface IResponse {
  buyerId: string;
  sellerId: string;
  corporateName: string;
  federalTaxId: string;
  workspaceQuantity: number;
  esocialConnected?: boolean;
}


function getInitials(corporateName: string) {
  const firstLetter = corporateName?.split(" ")?.[0]?.split("")?.[0]?.toUpperCase();
  const secondLetter = corporateName?.split(" ")?.[1]?.split("")?.[0]?.toUpperCase();

  if (!firstLetter && !secondLetter) return "AB";

  if (!secondLetter) return firstLetter;

  if (!firstLetter) return secondLetter;

  return `${firstLetter}${secondLetter}`;
}


/**
 * /workspaces/:orgId (errado)
 * API -> List all Seller's invites to buyers + number of workspaces created with them.
 */
export function WorkspaceList() {
  const user = useAuth();
  const history = useHistory();

  const [search, setSearch] = useState("");
  const [esocialFilter, setEsocialFilter] = useState<"TODOS" | "CONECTADO" | "NAO_CONECTADO">("TODOS");

  const allowedOrgs = ["REVOLUTION DO BRASIL ADAPTACAO VEICULAR LTDA", "A TOUAREG CORRETORA DE SEGUROS S.A.", "MULTIRISK ASSESSORIA E CORRETORA DE SEGUROS LTDA", "TAMIN CORPORATE CORRETORA DE SEGUROS LTDA"];

  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => api.get(`/v2/organizations/${user.user?.organization?.cnpj}/contracts`),
    // select: (data) => data.data.data as IResponse[],
    //@ts-ignore
    select: (data) => data.data.data.filter(d => {
      if (user.user?.organization?.email === "vida.previdencia@touaregseguros.com") {
        if (allowedOrgs.includes(d?.corporateName)) {
          return true;
        } else {
          return false;
        }
      } else {
        return d;
      }
    }) as IResponse[],
    enabled: user?.user?.isSeller === true, // Só faz a requisição se for seller
  });

  useEffect(() => {
    if (user?.user?.isSeller === false) {
      history.push("/");
    }
  }, [user?.user?.isSeller, history])

  return (
    <Page title="Contratos">
      <Page.Tab name="Listagem">
        <>
          {!isFetched && isFetching && <div>Loading...</div>}
          {!isFetched && isError && <div>Error...</div>}
          {data && user?.user?.isSeller === true && (
            <>
              <div style={{ display: "flex", flexDirection: "row", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                <StyledTextField
                  value={search}
                  placeholder="Digite para pesquisar"
                  label="Nome ou CNPJ do cliente"
                  onChange={(e) => setSearch(e.target.value.toLowerCase())}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" style={{ width: "24px", aspectRatio: "1" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl style={{ minWidth: 180 }}>
                  <InputLabel id="esocial-filter-label">Conexão eSocial</InputLabel>
                  <Select
                    labelId="esocial-filter-label"
                    label="Conexão eSocial"
                    value={esocialFilter}
                    onChange={(e) => setEsocialFilter(e.target.value as typeof esocialFilter)}
                    style={{ height: "56px" }}
                  >
                    <MenuItem value="TODOS">Todos</MenuItem>
                    <MenuItem value="CONECTADO">Conectado</MenuItem>
                    <MenuItem value="NAO_CONECTADO">Não conectado</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
                <Table>
                  <TableRow style={{ height: '70px'}}>
                    <TableCell style={{ width: "500px"}}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'rgb(111, 111, 120)', fontWeight: '600' }}>
                          Cliente
                      </span>
                    </TableCell>
                    <TableCell style={{ width: "60%"}}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'rgb(111, 111, 120)', fontWeight: '600' }}>
                        Contrato(s)
                      </span>
                    </TableCell>
                    <TableCell>
                      <span style={{ whiteSpace: 'nowrap', color: 'rgb(111, 111, 120)', fontWeight: '600' }}>
                        eSocial
                      </span>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                    {data
                      .filter(org => !search ? true : org.corporateName.toLowerCase().includes(search) || org.federalTaxId.toLowerCase().includes(search))
                      .filter(org => {
                        if (esocialFilter === "TODOS") return true;
                        if (esocialFilter === "CONECTADO") return org.esocialConnected === true;
                        return org.esocialConnected === false;
                      })
                      .map((org) => (
                      <TableRow key={org.buyerId} style={{ height: '70px'}}>
                        <TableCell>
                          <div style={{display: "flex", flexDirection: "row", gap: "8px"}}>
                            <Avatar alt="Avatar" variant="circular" style={{ width: "40px" }}>{getInitials(org.corporateName)}</Avatar>
                            <div style={{display: "flex", flexDirection: "column"}}>
                              <Title>{org.corporateName}</Title>
                              <Subtitle>{formatCnpj(org.federalTaxId)}</Subtitle>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{org.workspaceQuantity ?? 0}</TableCell>
                        <TableCell>
                          {org.esocialConnected === true && (
                            <Chip label="Conectado" size="small" style={{ backgroundColor: "#2E7D3214", color: "#2E7D32", fontWeight: 500 }} />
                          )}
                          {org.esocialConnected === false && (
                            <Chip label="Não conectado" size="small" style={{ backgroundColor: "#D32F2F14", color: "#D32F2F", fontWeight: 500 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" style={{ cursor: 'pointer' }} onClick={() => history.push(`/v2/organizations/contracts/${org.sellerId}/${org.buyerId}`)}>
                            <ChevronRight />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </Table>
              </TableContainer>
            </>
          )}
        </>
      </Page.Tab>
    </Page>
  );
}
